/**
 * ダッシュボードコンポーネント
 *
 * ユーザーのメイン画面
 * - 統計カード（保存ページ数、AI検索回数、プラン）
 * - 最近の保存ページ
 * - 広告エリア（無料ユーザーのみ）
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useUser } from '../../contexts/UserContext';
import { getRecentPages, getPageCount } from '../../services/pages';
import { useT } from '../../i18n';
import StatsCard from './StatsCard';
import RecentPages from './RecentPages';
import AdBanner from '../Ads/AdBanner';

function Dashboard() {
  const { user } = useAuth();
  const { userData, isPremium, shouldShowAds, remainingQueries, remainingPages } = useUser();
  const [recentPages, setRecentPages] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const { t } = useT();

  // データを読み込む
  useEffect(() => {
    // ユーザー切り替え時にデータをリセット
    setRecentPages([]);
    setPageCount(0);
    setLoading(true);

    if (user) {
      loadDashboardData();
    }
  }, [user]);

  /**
   * ダッシュボードデータを読み込む
   */
  async function loadDashboardData() {
    try {
      const [pages, count] = await Promise.all([
        getRecentPages(user.uid, 10),
        getPageCount(user.uid)
      ]);
      setRecentPages(pages);
      setPageCount(count);
    } catch (error) {
      console.error('ダッシュボードデータ読み込みエラー:', error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fade-in">
      {/* ウェルカムメッセージ */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">
          {user?.displayName
            ? t('dashboard.greeting', { name: user.displayName.split(' ')[0] })
            : t('dashboard.greetingDefault')}
        </h1>
        <p className="text-gray-500 mt-1">
          {t('dashboard.subtitle')}
        </p>
      </div>

      {/* 統計カード */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {/* 保存ページ数 */}
        <StatsCard
          title={t('dashboard.savedPages')}
          value={pageCount}
          max={userData?.limits?.maxPages || 1000}
          unit={t('dashboard.unit')}
          icon="📄"
          color="blue"
        />

        {/* AI検索回数 */}
        <StatsCard
          title={t('dashboard.aiSearch')}
          value={userData?.monthlyQueryCount || 0}
          max={userData?.limits?.monthlyQueries || 15}
          unit={t('dashboard.unitPerMonth')}
          icon="🔍"
          color="purple"
          showRewardButton={!isPremium && remainingQueries <= 3}
        />

        {/* プラン */}
        <StatsCard
          title={t('dashboard.plan')}
          value={isPremium ? 'Pro' : 'Free'}
          icon={isPremium ? '⭐' : '🆓'}
          color={isPremium ? 'primary' : 'gray'}
          showUpgradeButton={!isPremium}
        />
      </div>

      {/* 広告エリア（無料ユーザーのみ） */}
      {shouldShowAds && (
        <div className="mb-8">
          <AdBanner position="dashboard-top" />
        </div>
      )}

      {/* 最近の保存 */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-900">{t('dashboard.recentSaves')}</h2>
          <Link
            to="/pages"
            className="text-sm text-primary hover:underline"
          >
            {t('dashboard.viewAll')}
          </Link>
        </div>

        {loading ? (
          <div className="card p-8 text-center">
            <div className="spinner mx-auto mb-4" />
            <p className="text-gray-500">{t('common.loading')}</p>
          </div>
        ) : (
          <RecentPages pages={recentPages} />
        )}
      </section>

      {/* クイックスタートガイド（ページが少ない場合） */}
      {!loading && pageCount < 5 && (
        <section className="mt-8">
          <div className="card bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
            <h3 className="font-bold text-gray-900 mb-3">
              {t('dashboard.gettingStarted')}
            </h3>
            <ol className="space-y-3 text-sm text-gray-600">
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-xs font-bold">1</span>
                <span>{t('dashboard.step1')}</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-xs font-bold">2</span>
                <span>{t('dashboard.step2')}</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-xs font-bold">3</span>
                <span>{t('dashboard.step3')}</span>
              </li>
            </ol>
          </div>
        </section>
      )}
    </div>
  );
}

export default Dashboard;
